export const DIAGNOSTIC_LIMITS = { LCP: [2500, 4000], INP: [200, 500], CLS: [0.1, 0.25], TTFB: [800, 1800] };
const devices = ['PHONE', 'DESKTOP', 'TABLET'];
const day = 86400000;
const finite = value => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0 ? Number(value) : null;
export const diagnosticStatus = (metric, value) => value === null ? 'unknown'
  : value <= DIAGNOSTIC_LIMITS[metric][0] ? 'good'
    : value <= DIAGNOSTIC_LIMITS[metric][1] ? 'needs-improvement' : 'poor';

export function buildDiagnosticCandidates(byFormFactor, trends = []) {
  return devices.flatMap(device => Object.keys(DIAGNOSTIC_LIMITS).map(metric => {
    const data = byFormFactor?.[device];
    const raw = data?.metrics?.[metric];
    const value = data?.error ? null : finite(raw?.value ?? raw?.p75 ?? raw?.percentiles?.p75);
    const status = diagnosticStatus(metric, value);
    const trend = trends.find(item => item.device === device && item.metric === metric);
    return {
      metric, device, value, status,
      reason: ['poor', 'needs-improvement'].includes(status) ? 'failing' : trend ? 'trend' : 'manual',
      ...(trend ? { trend: trend.trend } : {})
    };
  }));
}

/**
 * Origin-only, global CrUX API 28-day windows. This is a conservative warning
 * heuristic, NOT statistical significance or a prediction of future p75.
 * Four samples over >=21 days, >=5% and metric-specific absolute increase,
 * positive median pairwise slope, >=2 rises and no substantial reversal.
 */
export function detectMetricTrends(response, { now = Date.now() } = {}) {
  if (response?.scope !== 'origin' || !response?.success) return [];
  const result = [];
  for (const device of devices) for (const metric of Object.keys(DIAGNOSTIC_LIMITS)) {
    const seen = new Set();
    const points = (response.history || []).flatMap(row => {
      const provenance = row.provenance?.[device];
      const time = Date.parse(row.date);
      const start = Date.parse(provenance?.startDate);
      const value = finite(row.devices?.[device]?.[metric]?.p75);
      if (provenance?.source !== 'crux_api' || provenance.countryId != null
        || provenance.connectionTypeId != null || !Number.isFinite(time)
        || !Number.isFinite(start) || Math.round((time - start) / day) !== 27
        || time > now || now - time > 60 * day || value === null) return [];
      return [{ value, time, date: new Date(time).toISOString().slice(0, 10) }];
    }).sort((a, b) => a.time - b.time).filter(point => {
      if (seen.has(point.date)) return false;
      seen.add(point.date);
      return true;
    });
    if (points.length < 4) continue;
    const first = points[0], last = points.at(-1);
    if (now - last.time > 14 * day || last.time - first.time < 21 * day || first.value <= 0) continue;
    const delta = last.value - first.value;
    const percent = delta / first.value * 100;
    if (percent < 5 || delta < { LCP: 100, INP: 8, CLS: 0.01, TTFB: 50 }[metric]) continue;
    let rises = 0, reversal = false, gap = false;
    const slopes = [];
    points.forEach((point, i) => {
      if (i) {
        const previous = points[i - 1];
        if (point.value > previous.value) rises++;
        if (previous.value - point.value > delta * 0.35) reversal = true;
        if (point.time - previous.time > 21 * day) gap = true;
      }
      for (let j = i + 1; j < points.length; j++) slopes.push((points[j].value - point.value) / (points[j].time - point.time));
    });
    slopes.sort((a, b) => a - b);
    if (rises < 2 || reversal || gap || slopes[Math.floor(slopes.length / 2)] <= 0) continue;
    result.push({
      metric, device, value: last.value, status: diagnosticStatus(metric, last.value), reason: 'trend',
      trend: {
        baseline: first.value, value: last.value, delta, percent,
        startDate: first.date, endDate: last.date, sampleCount: points.length,
        scope: 'origin', source: 'crux', windowDays: 28,
        nearLimit: last.value >= DIAGNOSTIC_LIMITS[metric][0] * 0.9 && last.value <= DIAGNOSTIC_LIMITS[metric][0]
      }
    });
  }
  return result;
}

export function formatTrendSummary(item, language = 'cs') {
  const t = item.trend;
  if (!t) return '';
  const cs = language === 'cs';
  const format = value => new Intl.NumberFormat(cs ? 'cs-CZ' : 'en-US', { maximumFractionDigits: item.metric === 'CLS' ? 3 : 1 }).format(value);
  const date = value => new Date(`${value}T00:00:00Z`).toLocaleDateString(cs ? 'cs-CZ' : 'en-US', { timeZone: 'UTC' });
  const unit = item.metric === 'CLS' ? '' : ' ms';
  const boundary = t.nearLimit ? (cs ? ' Blízko hranice dobrého pásma.' : ' Near the good-band limit.') : '';
  return `${date(t.startDate)} – ${date(t.endDate)}: ${format(t.baseline)} → ${format(t.value)}${unit} (+${format(t.percent)} %).${boundary} ${t.sampleCount} ${cs ? 'snapshotů' : 'snapshots'}. ${cs
    ? 'Překrývající se 28denní okna, nikoli jednotlivé dny. Trend neprokazuje příčinu.'
    : 'Overlapping 28-day windows, not individual days. The trend does not establish a cause.'}`;
}
